import {useState, useEffect, useCallback, useRef} from 'react';
import {useSelector} from 'react-redux';
import {useLazyGetPDFAuthTokenQuery} from '../../../data/documentWorkspaceApi';

// Refresh 5 minutes before expiration
const TOKEN_REFRESH_BUFFER_MS = 5 * 60 * 1000;

export const usePdfAuthToken = (documentId) => {
  const [token, setToken] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const {isAuthenticated} = useSelector((state) => state.session);

  const refreshTimerRef = useRef(null);
  const isMountedRef = useRef(true);

  const [fetchToken] = useLazyGetPDFAuthTokenQuery();

  const clearRefreshTimer = useCallback(() => {
    if (refreshTimerRef.current) {
      clearTimeout(refreshTimerRef.current);
      refreshTimerRef.current = null;
    }
  }, []);

  const scheduleRefresh = useCallback(
    (expiresAt, refreshFn) => {
      clearRefreshTimer();

      const refreshAt = expiresAt - TOKEN_REFRESH_BUFFER_MS;
      const delay = Math.max(0, refreshAt - Date.now());

      refreshTimerRef.current = setTimeout(() => {
        if (isMountedRef.current) {
          refreshFn();
        }
      }, delay);
    },
    [clearRefreshTimer]
  );

  const fetchAuthToken = useCallback(async () => {
    if (!documentId) {
      return null;
    }

    setIsLoading(true);
    setError(null);

    try {
      const result = await fetchToken({documentId}).unwrap();
      const token = result.token;
      const expiresIn = result.expiresIn; // in seconds

      if (!isMountedRef.current) return null;

      setToken(token);
      setIsLoading(false);

      // scheduling automatic token refresh
      const expiresAt = Date.now() + expiresIn * 1000;
      scheduleRefresh(expiresAt, fetchAuthToken);

      return token;
    } catch (err) {
      if (!isMountedRef.current) return null;

      console.error('[usePdfAuthToken] Failed to fetch token:', err);
      setError(err);
      setIsLoading(false);
      return null;
    }
  }, [documentId, fetchToken, scheduleRefresh]);

  // fetch token on mount or when documentId/isAuthenticated changes
  // isAuthenticated check prevents the API call before PropelAuth resolves
  // which would send a request without an Authorization header and get a 401
  useEffect(() => {
    isMountedRef.current = true;

    if (documentId && isAuthenticated) {
      fetchAuthToken();
    }

    return () => {
      isMountedRef.current = false;
      clearRefreshTimer();
    };
  }, [documentId, isAuthenticated]); // don't include fetchAuthToken to avoid loops

  // reset when documentId changes
  useEffect(() => {
    return () => {
      setToken(null);
      setError(null);
      clearRefreshTimer();
    };
  }, [documentId, clearRefreshTimer]);

  return {
    token,
    isLoading,
    error,
    refetch: fetchAuthToken,
    isAuthenticated: !!token && !error,
  };
};

export default usePdfAuthToken;
